import React, { useState } from 'react';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b-2 border-yellow-200 dark:border-[#594400] overflow-hidden transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex justify-between items-center text-left focus:outline-none group"
      >
        <h4 className="text-xl md:text-2xl font-bold text-[#594400] dark:text-[#FFD54F] group-hover:text-orange-500 dark:group-hover:text-[#FFE082] transition-colors">
          {question}
        </h4>
        <span className={`transform transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180' : ''}`}>
          <svg className="w-6 h-6 text-yellow-500 dark:text-[#FFD54F]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      
      <div 
        className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[800px] opacity-100 mb-6' : 'max-h-0 opacity-0'}`}
      >
        <div className="text-yellow-900 dark:text-[#FFE082]/80 text-lg font-medium leading-relaxed">
          {answer}
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const faqs = [
    {
      question: "¿Cómo realizo una compra en la página?",
      answer: (
        <div className="space-y-4">
          <p>Comprar es muy sencillo y seguro siguiendo estos pasos:</p>
          <ol className="list-decimal ml-5 space-y-2">
            <li>Explora nuestro catálogo y añade tus productos favoritos al carrito.</li>
            <li>Abre tu carrito, revisa tu pedido e ingresa tus datos de envío (Nombre, dirección y teléfono).</li>
            <li>Haz clic en el botón de finalizar pedido para generar el mensaje automático hacia nuestro <strong>WhatsApp oficial</strong>.</li>
            <li>Coordinaremos los detalles finales, cotizaremos tu envío (si es nacional) y te daremos el total por ese medio.</li>
          </ol>
        </div>
      )
    },
    {
      question: "¿Cómo se calculan y manejan los envíos nacionales?",
      answer: (
        <div className="space-y-4">
          <p>Es importante saber que los precios de los productos <strong>no incluyen gastos de envío</strong>. El proceso para cotizar es el siguiente:</p>
          <div className="p-5 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-xl">
            <p className="font-bold mb-2">Cotización por peso y volumen:</p>
            <p>Para darte un precio exacto, necesitamos recibir tu pedido por WhatsApp. El costo varía dependiendo del <strong>peso total y las dimensiones de la caja</strong> de los productos que elijas.</p>
          </div>
          <div className="p-5 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-xl">
            <p className="font-bold mb-2">Paqueterías disponibles:</p>
            <p>Trabajamos con diversas empresas para que elijas la que más te convenga o la de menor costo: <strong>Estafeta, DHL, FedEx, ampm</strong>, entre otras.</p>
          </div>
        </div>
      )
    },
    {
      question: "¿Cuáles son los métodos de pago y políticas de entrega local?",
      answer: (
        <div className="space-y-4">
          <div className="p-4 bg-orange-100 dark:bg-orange-900/30 border-l-4 border-orange-500 rounded-r-lg">
            <p className="font-bold text-orange-800 dark:text-orange-400 mb-1">Pagos para Envíos Nacionales:</p>
            <p><strong>No solicitamos pagos directamente en la web.</strong> Una vez que tengamos tu pedido y la cotización del envío, te proporcionaremos los datos bancarios oficiales vía WhatsApp.</p>
          </div>
          <div className="p-4 bg-green-100 dark:bg-green-900/30 border-l-4 border-green-500 rounded-r-lg">
            <p className="font-bold text-green-800 dark:text-green-400 mb-1">Entregas en Zacoalco de Torres (¡Envío Gratis!):</p>
            <p>Para nuestros clientes en <strong>Zacoalco de Torres</strong>, la entrega a domicilio es <strong>completamente gratis</strong> en compras mínimas de <strong>$120.00 MXN</strong>. No aplica ningún costo adicional de entrega y puedes realizar tu <strong>pago en efectivo al recibir tu pedido</strong>.</p>
          </div>
        </div>
      )
    },
    {
      question: "¿Sus productos son 100% naturales?",
      answer: "Totalmente. Nuestra miel y derivados son recolectados y envasados de forma artesanal, sin procesos industriales ni aditivos químicos, conservando todas sus propiedades naturales de la colmena."
    },
    {
      question: "¿Manejan ventas por mayoreo?",
      answer: "Sí, contamos con precios especiales para mayoristas. Solicita información detallada a través de WhatsApp para conocer nuestras condiciones y catálogo de mayoreo."
    },
    {
      question: "¿Cómo adquiero material apícola (abejas reinas, colmenas, núcleos y madera)?",
      answer: (
        <div className="space-y-4">
          <p>Para adquirir este tipo de material especializado, el proceso es el siguiente:</p>
          <ul className="list-disc ml-5 space-y-2">
            <li>Es necesario <strong>solicitar una cotización</strong> para verificar la disponibilidad actual.</li>
            <li>En caso de poder realizar el pedido, se requiere un <strong>50% de anticipo</strong> (ya sea en efectivo o transferencia).</li>
            <li>Una vez recibido el anticipo, te enviamos un <strong>comprobante de pago digital firmado</strong> y la fecha estimada de entrega. En caso de tener que posponer la fecha por causas externas, se te notificará con tiempo.</li>
            <li><strong>Entrega:</strong> Este material solo se entrega en <strong>tienda física</strong>. No contamos con envío nacional para abejas o madera; solo realizamos entregas locales o regionales con un <strong>costo adicional de entrega</strong>.</li>
          </ul>
        </div>
      )
    },
    {
      question: "¿Ofrecen facturación por mis compras?",
      answer: "Sí, podemos emitir factura exclusivamente en la compra de miel, colmenas y núcleos (o cualquier producto relacionado directamente con las abejas)."
    }
  ];

  return (
    <section id="faq" className="py-24 bg-white dark:bg-[#1C1B17] transition-colors duration-300 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-[#594400] dark:text-[#FFD54F] mb-6 uppercase tracking-tight">
            Preguntas Frecuentes
          </h2>
          <div className="w-24 h-2 bg-yellow-500 dark:bg-[#FFD54F] mx-auto rounded-full mb-6"></div>
          <p className="text-xl text-yellow-900 dark:text-[#FFE082]/80 font-medium italic">
            "Todo lo que necesitas saber para adquirir nuestros productos artesanales."
          </p>
        </div>

        <div className="bg-[#FFFBF0] dark:bg-[#2A2821] p-8 md:p-12 rounded-[2.5rem] shadow-sm border-2 border-yellow-100 dark:border-[#594400]">
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <button 
            onClick={() => window.history.back()}
            className="text-[#594400] dark:text-[#FFD54F] font-bold hover:underline flex items-center justify-center gap-2 mx-auto"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Volver a la tienda
          </button>
        </div>

      </div>
    </section>
  );
};

export default FAQ;